import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../utils/firebase';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          const res = await api.post('/auth/verify');
          setUserData(res.data.user);
          setTenant(res.data.tenant);
          setPricing(res.data.pricing);
        } catch (error) {
          console.error('Auth verify failed:', error);
          // Sign out of Firebase if server doesn't recognize this user
          await signOut(auth);
          setFirebaseUser(null);
          setUserData(null);
          setTenant(null);
        }
      } else {
        setUserData(null);
        setTenant(null);
        setPricing(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  };

  const logout = async () => {
    await signOut(auth);
    setUserData(null);
    setTenant(null);
    setPricing(null);
  };

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const refreshTenant = async () => {
    try {
      const res = await api.get('/auth/me');
      setTenant(res.data.tenant);
      setPricing(res.data.pricing);
    } catch (error) {
      console.error('Refresh tenant failed:', error);
    }
  };

  const value = {
    firebaseUser,
    user: userData,
    tenant,
    pricing,
    loading,
    login,
    logout,
    resetPassword,
    refreshTenant,
    isAuthenticated: !!firebaseUser && !!userData,
    isSuperAdmin: userData?.role === 'super_admin',
    isTenantAdmin: userData?.role === 'tenant_admin' || userData?.role === 'super_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
